import React from 'react';
import PropTypes from 'prop-types';

import {Box, Divider, Stack, Typography, Chip} from 'views/components';
import {TaskEditor, Translate} from 'views/module';

import TaskView from './TaskView';

const TaskList = (props) => {
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editingTaskRn, setEditingTaskRn] = React.useState(null);
  const [activeFilter, setActiveFilter] = React.useState('all');

  const clientTasks = props.clientTasks;
  const clientBackend = props.clientBackend;

  const groupedTasks = React.useMemo(() => {
    const currentDate = new Date();
    const now = Date.now();
    const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();

    return clientTasks.reduce((result, task) => {
      if (task.status === 'COMPLETE') {
        result.completed.push(task);
        return result;
      }

      if (!task.dueDate) {
        result.dueLater.push(task);
        return result;
      }

      const dueDate = new Date(task.dueDate);
      const dueTime = dueDate.getTime();
      const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()).getTime();

      if (dueTime < now) {
        result.overdue.push(task);
      } else if (dueDay === today) {
        result.dueToday.push(task);
      } else {
        result.dueLater.push(task);
      }

      return result;
    }, {
      overdue: [],
      dueToday: [],
      dueLater: [],
      completed: [],
    });
  }, [clientTasks]);

  const sections = [
    {
      key: 'overdue',
      title: 'client.ClientDetail.Tasks.Sections.Overdue',
      color: 'error.main',
      tasks: groupedTasks.overdue,
    },
    {
      key: 'dueToday',
      title: 'client.ClientDetail.Tasks.Sections.DueToday',
      color: '#6B6760',
      tasks: groupedTasks.dueToday,
    },
    {
      key: 'dueLater',
      title: 'client.ClientDetail.Tasks.Sections.DueLater',
      color: '#6B6760',
      tasks: groupedTasks.dueLater,
    },
    {
      key: 'completed',
      title: 'client.ClientDetail.Tasks.Sections.Completed',
      color: '#6B6760',
      tasks: groupedTasks.completed,
    },
  ].filter((section) => section.tasks.length > 0);

  const filters = [
    {key: 'all', title: 'client.ClientDetail.Tasks.Filters.All'},
    {key: 'overdue', title: 'client.ClientDetail.Tasks.Sections.Overdue'},
    {key: 'dueToday', title: 'client.ClientDetail.Tasks.Sections.DueToday'},
    {key: 'dueLater', title: 'client.ClientDetail.Tasks.Sections.DueLater'},
    {key: 'completed', title: 'client.ClientDetail.Tasks.Sections.Completed'},
  ];

  const visibleSections = React.useMemo(() => {
    if (activeFilter === 'all') {
      return sections;
    }

    return sections.filter((section) => section.key === activeFilter);
  }, [activeFilter, sections]);

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingTaskRn(null);
  };

  const addTask = () => {
    setEditingTaskRn(null);
    setIsEditorOpen(true);
  };

  const editTask = (task) => {
    setIsEditorOpen(false);
    setEditingTaskRn(task.taskRn);
  };

  const edits = {
    addTask: (taskData) => {
      const complete = () => {
        setIsEditorOpen(false);
      };
      clientBackend.tasks.addTask(taskData, complete);
    },
    setTaskStatusToComplete: (taskRn, isComplete) => {
      if (isComplete) {
        return;
      }

      clientBackend.tasks.updateTaskStatus(taskRn, 'COMPLETE');
    },
    editTask: (taskRn, taskData) => {
      const complete = () => {
        setEditingTaskRn(null);
      };
      clientBackend.tasks.updateTask(taskRn, taskData, complete);
    },
    removeTask: (taskData) => {
      const complete = () => {
        setEditingTaskRn((previousTaskRn) => (previousTaskRn === taskData.taskRn ? null : previousTaskRn));
      };
      clientBackend.tasks.removeTask(taskData.taskRn, complete);
    },
  };

  const renderTask = (task, index) => {
    return editingTaskRn === task.taskRn ?
      <Box key={task.taskRn || index} sx={{mb: 1, px: 2, pt: 1}}>
        <TaskEditor
          isEditing
          task={task}
          onCancel={closeEditor}
          onSubmit={(payload) => edits.editTask(task.taskRn, payload)}
        />
      </Box> :
      <TaskView
        key={task.taskRn || index}
        task={task}
        handleSetStatusToComplete={edits.setTaskStatusToComplete}
        onClickEditTask={editTask}
        removeTask={edits.removeTask}
      />;
  };

  return (
    <Stack>
      <Stack
        direction='row'
        spacing={1}
        sx={{
          px: 2,
          pt: 2,
          pb: sections.length > 0 ? 1 : 0,
          flexWrap: 'wrap',
        }}
      >
        {
          filters.map((filter) => {
            const isActive = activeFilter === filter.key;

            return (
              <Chip
                key={filter.key}
                component='button'
                size='small'
                onClick={() => setActiveFilter(filter.key)}
                label={<Translate expression={filter.title} />}
                sx={{
                  backgroundColor: isActive ? '#FFF7F4' : '#F7F4EF',
                  border: '1px solid',
                  borderColor: isActive ? '#EBBDAD' : '#E8E4DE',
                  fontFamily: '"Roboto Mono", monospace',
                  color: isActive ? '#DA7955' : '#6B6760',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              />
            );
          })
        }
      </Stack>
      {
        visibleSections.map((section, index) => (
          <React.Fragment key={section.key}>
            {
              index > 0 && <Divider />
            }
            <Stack>
              <Typography
                variant='subtitle2'
                sx={{
                  px: 2,
                  pt: 2,
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: section.color,
                }}
              >
                <Translate expression={section.title} />
              </Typography>
              {section.tasks.map(renderTask)}
            </Stack>
          </React.Fragment>
        ))
      }
      {
        isEditorOpen ?
          <Box sx={{mt: 1, px: 2}}>
            <TaskEditor
              onCancel={closeEditor}
              onSubmit={edits.addTask}
            />
          </Box> :
          <Box sx={{px: 2, py: 1}}>
            <Box
              component='button'
              onClick={addTask}
              sx={{
                background: 'none',
                border: 'none',
                color: 'primary.main',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                p: 0,
              }}
            >
              <Box component='span'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Box>
          </Box>
      }
    </Stack>
  );
};

TaskList.propTypes = {
  clientRn: PropTypes.string.isRequired,
  clientData: PropTypes.object,
  clientTasks: PropTypes.array.isRequired,
  clientBackend: PropTypes.object.isRequired,
};

export default React.memo(TaskList);
